import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Instagram, LogOut, User, Shuffle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ChessLogo } from "@/components/ChessLogo";
import { Avatar } from "@/components/Avatar";
import { CoinBadge } from "@/components/CoinBadge";
import { AuthModal } from "@/components/AuthModal";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const INSTA = "https://instagram.com/dimaginationaljanatadal";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [authOpen, setAuthOpen] = useState(false);

  const links = [
    { label: "Feed", to: "/feed" },
    { label: "Mission", to: "/#mission" },
    { label: "Follow", to: "/#follow" },
  ];

  const go = (to) => {
    if (to.startsWith("/#")) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.querySelector(to.slice(1))?.scrollIntoView({ behavior: "smooth" }), 200);
      } else {
        document.querySelector(to.slice(1))?.scrollIntoView({ behavior: "smooth" });
      }
    } else navigate(to);
  };

  return (
    <>
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="tricolor-bar h-1 w-full" />
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <button
            data-testid="navbar-brand-logo"
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 group"
          >
            <ChessLogo size={34} />
            <div className="text-left leading-none">
              <div className="font-display font-extrabold text-base tracking-tight">DNZD</div>
              <div className="text-[10px] text-muted-foreground font-mono tracking-wider hidden sm:block">
                DIMAGI NATIONAL ZANATA DAL
              </div>
            </div>
          </button>

          <nav data-testid="navbar-nav-links" className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.to)}
                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              data-testid="navbar-instagram-link"
              href={INSTA}
              target="_blank"
              rel="noreferrer"
              className="grid place-items-center h-9 w-9 rounded-lg border border-border text-muted-foreground hover:text-saffron hover:border-saffron/50 transition-colors"
              aria-label="DNZD Instagram"
            >
              <Instagram size={18} />
            </a>

            {user ? (
              <>
                <button onClick={() => navigate("/feed")}>
                  <CoinBadge value={user.coins} testId="navbar-coin-balance" />
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="navbar-profile-button" className="ml-1">
                      <Avatar seed={user.avatar_seed} alias={user.alias} size={36} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <div className="text-sm font-semibold">{user.alias}</div>
                      <div className="text-xs text-muted-foreground font-mono">{user.coins} Dimagi coins</div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="menu-my-profile" onClick={() => navigate(`/profile/${user.id}`)}>
                      <User size={15} className="mr-2" /> My Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                      <LogOut size={15} className="mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  data-testid="navbar-login-button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setAuthOpen("login")}
                  className="hidden sm:inline-flex"
                >
                  Log in
                </Button>
                <Button
                  data-testid="navbar-join-button"
                  size="sm"
                  onClick={() => setAuthOpen("register")}
                  className="bg-saffron text-primary-foreground hover:bg-saffron/90 font-semibold gap-1.5"
                >
                  <Shuffle size={15} /> Join Free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>
      <AuthModal open={!!authOpen} tab={authOpen || "register"} onOpenChange={(v) => setAuthOpen(v ? authOpen : false)} />
    </>
  );
}
